import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { parseAsArrayOf, parseAsString, parseAsStringEnum, useQueryState } from 'nuqs';
import { useId, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { useInfiniteSocialMediaPosts, useUserQuery } from '../../../../queries';
import { filterCancelledSocialPosts } from '../../../../utils/socialMediaFiltering';
import { Button } from '../../../components-v2/Button';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../components-v2/Dropdown';
import { Select, SelectCheckboxItem, SelectContent, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { bs } from '../../../styles/styles';
import { UserSelectModal } from '../../UserSelectModal';
import { SocialMediaReportingTableRow } from './presentation';
import { SocialMediaReportingTable } from './SocialMediaReportingTable';
import { styleSheet } from './styles';

export const allReportStatusValues = Object.values(Api.ReportPostStatus);

export const mapReportPostStatusToPostStatus = (status: Api.ReportPostStatus[]) => {
	let postStatus: Api.PostStatus[] | null = null;
	if (status.length < allReportStatusValues.length) {
		postStatus = [];
		status.forEach(s => {
			switch (s) {
				case Api.ReportPostStatus.Scheduled:
					postStatus.push(Api.PostStatus.Scheduled);
					break;
				case Api.ReportPostStatus.Cancelled:
					postStatus.push(Api.PostStatus.Cancelled);
					break;
				case Api.ReportPostStatus.Failed:
					postStatus.push(Api.PostStatus.Failed);
					break;
				case Api.ReportPostStatus.Pending:
					postStatus.push(Api.PostStatus.Pending);
					break;
				case Api.ReportPostStatus.Posted:
					postStatus = [
						...postStatus,
						Api.PostStatus.PartiallySucceeded,
						Api.PostStatus.Succeeded,
						Api.PostStatus.Started,
					];
					break;
				default:
					break;
			}
		});
	}
	return postStatus;
};

export const UserSocialMediaReportingTable = () => {
	const id = useId();
	const userSession = useUserSession();
	const fullscreenModal = useFullscreenModal();
	const [showingSentByModal, setShowingSentByModal] = useState(false);
	const [status, setStatus] = useQueryState(
		'status',
		parseAsArrayOf(parseAsStringEnum<Api.ReportPostStatus>(allReportStatusValues)).withDefault(allReportStatusValues)
	);
	const [userId, setUserId] = useQueryState('userId', parseAsString);
	const userQuery = useUserQuery({
		enabled: Boolean(userId) && userId !== userSession.user.id,
		id: userId,
	});
	const postStatus = mapReportPostStatusToPostStatus(status);
	let senderLabel = '...';
	if (!userId) {
		senderLabel = 'All Employees';
	} else if (userId === userSession.user.id) {
		senderLabel = 'Me';
	} else if (userQuery.data) {
		senderLabel = getDisplayName(userQuery.data);
	}
	const isCustomFilter = status.length !== allReportStatusValues.length || Boolean(userId);
	const socialMediaPostsQuery = useInfiniteSocialMediaPosts({
		selectedPostStatus: postStatus,
		meta: {
			errorLogging: {
				category: 'SocialMediaReportingTable',
				action: 'GetSocialReports-Error',
			},
		},
		selectedUserId: userId,
	});
	const handleStatusChanged = (value: Api.ReportPostStatus[]) => {
		if (value.length === 0) {
			setStatus(allReportStatusValues);
			return;
		}
		setStatus(value);
	};
	const onClearFilters = () => {
		setStatus(allReportStatusValues);
		setUserId(null);
	};
	const handleReportTitleClick = (report: Api.SocialMediaPostReportViewModel) => {
		fullscreenModal.history.push({
			pathname:
				report.status === Api.PostStatus.Pending
					? '/social-media/post/approval-preview'
					: `/social-media/post/edit-from-post-report`,
			state: { viewModel: report },
		});
	};

	return (
		<div>
			<div className={css(styleSheet.filter)}>
				{'Filter By:'}
				<Select multiple value={status} onValueChange={handleStatusChanged}>
					<SelectTrigger styles={[bs.w60]}>
						<SelectValue>{status.length === allReportStatusValues.length ? 'All' : status.join(', ')}</SelectValue>
					</SelectTrigger>
					<SelectContent>
						{allReportStatusValues.map(value => {
							return (
								<SelectCheckboxItem key={`${id}-status-${value}`} value={value}>
									{value}
								</SelectCheckboxItem>
							);
						})}
					</SelectContent>
				</Select>
				<Dropdown>
					<DropdownSelectTrigger styles={[bs.w60]}>Sent by {senderLabel}</DropdownSelectTrigger>
					<DropdownContent>
						{userSession.isAdmin ? (
							<DropdownItem
								onClick={() => {
									if (userId != null) {
										setUserId(null);
									}
								}}
							>
								All Employees
							</DropdownItem>
						) : null}
						<DropdownItem
							onClick={() => {
								if (userId !== userSession.user.id) {
									setUserId(userSession.user.id);
								}
							}}
						>
							Sent by Me
						</DropdownItem>
						{userSession.isAdmin ? (
							<DropdownItem onClick={() => setShowingSentByModal(true)}>Select an Employee</DropdownItem>
						) : null}
					</DropdownContent>
				</Dropdown>
				{isCustomFilter ? (
					<Button variant='secondary' onClick={onClearFilters}>
						Clear filters
					</Button>
				) : null}
			</div>
			<SocialMediaReportingTable postStatus={postStatus} userId={userId}>
				{socialMediaPostsQuery.status === 'success'
					? socialMediaPostsQuery.data.pages.map(group => {
							const isExplicitlyCancelledFilter = postStatus?.includes(Api.PostStatus.Cancelled);
							const filteredPosts = filterCancelledSocialPosts(group.values, isExplicitlyCancelledFilter);
							return filteredPosts.map(item => {
								const itemVm = new Api.SocialMediaPostReportViewModel(userSession, item);
								return (
									<SocialMediaReportingTableRow key={itemVm.id} report={itemVm} onTitleClick={handleReportTitleClick} />
								);
							});
						})
					: null}
			</SocialMediaReportingTable>
			{socialMediaPostsQuery.hasNextPage && !socialMediaPostsQuery.isFetchingNextPage ? (
				<Waypoint
					bottomOffset='-200px'
					onEnter={() => {
						socialMediaPostsQuery.fetchNextPage();
					}}
				/>
			) : null}
			<UserSelectModal
				modalProps={{
					isOpen: showingSentByModal,
					onRequestClose: (user?: Api.UserViewModel, cancel?: boolean) => {
						if (user && !cancel) {
							setUserId(user.id);
						}
						setShowingSentByModal(false);
					},
				}}
				title='Select an Employee'
			/>
		</div>
	);
};
